import { Component, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { of, throwError } from 'rxjs';
import { retryWhen, delay, take, tap, mergeMap } from 'rxjs/operators';
import { Staff } from 'src/models/staff.model';
import { StaffService } from 'src/services/staff/staff.service';

@Component({
  selector: 'app-bstp-team',
  templateUrl: './bstp-team.component.html',
  styleUrls: ['./bstp-team.component.scss']
})
export class BstpTeamComponent implements OnInit {
  staffs: Array<Staff>;
  search: string;
  
  // Nouvelles propriétés pour les filtres et l'affichage
  selectedDepartment = '';
  viewMode = 'grid';
  
  // Propriétés calculées pour les statistiques
  get uniqueDepartments() {
    if (!this.staffs) return [];
    // Utiliser le nom de la fonction comme département
    const departments = this.staffs
      .map(staff => staff.fonction?.name)
      .filter(dept => dept)
      .filter((dept, index, arr) => arr.indexOf(dept) === index);
    return departments;
  }
  
  get averageExperience() {
    if (!this.staffs || this.staffs.length === 0) return 0;
    // Simulation d'années d'expérience basée sur la longueur du nom de fonction
    const totalExperience = this.staffs.reduce((sum, staff) => {
      return sum + (staff.fonction?.name?.length || 0);
    }, 0);
    return 6;
  }
  
  get totalProjects() {
    if (!this.staffs) return 0;
    // Simulation du nombre de projets basée sur la longueur de la description
    return this.staffs.reduce((sum, staff) => {
      return sum + (staff.description?.length || 0);
    }, 0) / 100; // Division pour avoir un nombre raisonnable
  }

  constructor(public staffService: StaffService, private meta: Meta) {}

  ngOnInit(): void {
    this.meta.addTag({ name: 'description', content: "Equipe BSTP" });
    this.meta.addTag({ name: 'keywords', content: 'BSTP Guinée, BSTP, bstpgn team, Team BSTP, Equipe BSTP' });
    this.meta.addTag({ name: 'author', content: 'BSTP Guinée' });
    this.staffService.list().pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error) => {
            // Si c'est une erreur serveur (500+), on réessaie après un délai
            if (error.status >= 500) {
              console.log("Erreur serveur, nouvelle tentative...");
              return of(error).pipe(delay(500));
            }
            // Sinon (404, 400, etc.), on propage immédiatement l'erreur
            return throwError(() => error);
          }),
          take(3) // Max 3 tentatives pour les erreurs 5xx
        )
      )
    ).subscribe({
      next: (staffs: Array<Staff>) => {
        this.staffService.update(staffs); 
        this.staffs = staffs;
        this.animateCounters();
      },
      error: (error) => console.error(error),
    });
  }

  get filteredTeam() {
    let filtered = this.staffService.value || [];
    
    // Filtre par recherche
    if (this.search) {
      filtered = filtered.filter(staff =>
        staff.full_name.toLowerCase().includes(this.search.toLowerCase()) ||
        staff.fonction?.name?.toLowerCase().includes(this.search.toLowerCase())
      );
    }
    
    // Filtre par département (utiliser le nom de la fonction)
    if (this.selectedDepartment) {
      filtered = filtered.filter(staff => staff.fonction?.name === this.selectedDepartment);
    }
    
    return filtered;
  }
  
  // Nouvelles méthodes pour l'interactivité
  onSearchInput() {
    // La recherche se fait automatiquement via le getter filteredTeam
  }
  
  filterByDepartment() {
    // Le filtrage se fait automatiquement via le getter filteredTeam
  }
  
  clearFilters() {
    this.search = '';
    this.selectedDepartment = '';
  }
  
  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
  }
  
  getDepartmentName(department: string): string {
    // Retourner le nom du département tel quel
    return department;
  }
  
  viewProfile(staff: Staff) {
    // Navigation vers le profil détaillé du membre
    console.log('Voir profil:', staff);
  }
  
  contactMember(staff: Staff) {
    // Action pour contacter le membre
    console.log('Contacter:', staff);
  }
  
  animateCounters() {
    setTimeout(() => {
      const counters = document.querySelectorAll('.stat-number');
      counters.forEach(counter => {
        const target = parseInt(counter.getAttribute('data-count') || '0');
        const duration = 2000;
        const increment = target / (duration / 16);
        let current = 0;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }
          counter.textContent = Math.floor(current).toString();
        }, 16);
      });
    }, 500);
  }
}
